// src/context/CartContext.jsx
//
// Cart UI state is a mirror of the last Cart API response or agent cart snapshot.
// Manual mutations go through REST /api/cart. Agent mutations apply the agent
// payload directly (no Cart API call).
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  fetchCart,
  addCartLine,
  updateCartLine,
  removeCartLine,
  clearCartRemote,
} from "../api/cartApi";
import { normalizeAgentCartPayload } from "../utils/agentCartPayload";
import {
  trackCartChanged,
  isAgentActionActive,
  UI_EVENT_STALE_MS,
} from "../services/uiAgentSync";
import { useAuth } from "./AuthContext";
import { getUserProfile } from "../utils/auth";
import { showCartToast, showToast } from "../utils/toastManager";
import { flyToBag, productImageOf } from "../utils/flyToBag";

const CartContext = createContext();

function normalizeCartSizeLabel(size) {
  if (size == null || size === "") return "__";
  return String(size).trim().toUpperCase();
}

function extractItemSize(item) {
  if (item?.size != null && item.size !== "") return String(item.size);
  if (Array.isArray(item?.attributes?.size) && item.attributes.size.length > 0) {
    return String(item.attributes.size[0]);
  }
  if (typeof item?.attributes?.size === "string" && item.attributes.size) {
    return item.attributes.size;
  }
  return null;
}

function withLineUids(rows) {
  if (!Array.isArray(rows)) return [];
  return rows.map((item, idx) => ({
    ...item,
    cartLineUid:
      typeof item?.cartLineUid === "string" && item.cartLineUid.trim() !== ""
        ? item.cartLineUid
        : `line:${idx}:${item?.id ?? "?"}:${normalizeCartSizeLabel(item?.size ?? null)}`,
  }));
}

/** Stable ref for +/- / remove when multiple rows share the same product id. */
export function cartLineRefForUpdate(item) {
  if (!item) return null;
  if (item.cartLineUid != null && item.cartLineUid !== "") {
    return { cartLineUid: item.cartLineUid };
  }
  return item.id;
}

function lineIndexForRef(ref, prev) {
  if (ref != null && typeof ref === "object") {
    const uid = /** @type {{ cartLineUid?: string }} */ (ref).cartLineUid;
    if (uid != null && uid !== "") {
      return prev.findIndex((it) => it.cartLineUid === uid);
    }
    if ("id" in /** @type {object} */ (ref)) {
      const idStr = String(/** @type {{ id?: unknown }} */ (ref).id);
      return prev.findIndex((it) => it != null && String(it.id) === idStr);
    }
  }
  const idStr =
    typeof ref === "string" || typeof ref === "number" ? String(ref) : null;
  if (idStr == null) return -1;
  return prev.findIndex((it) => it != null && String(it.id) === idStr);
}

/** Pull `data.cart` from a CartApiResponse envelope. */
function cartSummaryFromApiResponse(apiRes) {
  if (!apiRes || typeof apiRes !== "object") return null;
  const data = apiRes.data;
  if (data != null && typeof data === "object" && data.cart) {
    return data.cart;
  }
  // Some gateways may return the summary at the top level
  if (Array.isArray(apiRes.items)) return apiRes;
  return null;
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const cartItemsRef = useRef(cartItems);

  useEffect(() => {
    cartItemsRef.current = cartItems;
  }, [cartItems]);

  // --- UI → Agent sync: queue events at mutation time, flush after commit ---
  const pendingUiEventsRef = useRef([]);
  const queueCartUiEvent = useCallback((event) => {
    pendingUiEventsRef.current.push({
      ...event,
      source: isAgentActionActive() ? "agent" : "manual",
      at: Date.now(),
    });
  }, []);

  useEffect(() => {
    if (!pendingUiEventsRef.current.length) return;
    const pending = pendingUiEventsRef.current;
    pendingUiEventsRef.current = [];
    const badgeCount = cartItems.reduce(
      (sum, item) => sum + (Number(item.quantity) || 0),
      0
    );
    const now = Date.now();
    pending.forEach(({ at, ...event }) => {
      if (now - at > UI_EVENT_STALE_MS) return;
      trackCartChanged({ ...event, cartBadgeCount: badgeCount });
    });
  }, [cartItems]);

  /** Apply a cart snapshot (API summary or agent payload) into UI state. */
  const applyCartSnapshot = useCallback((payload) => {
    const next = normalizeAgentCartPayload(payload);
    setCartItems(withLineUids(next));
    return true;
  }, []);

  const applyFromApiResponse = useCallback(
    (apiRes) => {
      const summary = cartSummaryFromApiResponse(apiRes);
      if (summary) {
        applyCartSnapshot(summary);
        return true;
      }
      return false;
    },
    [applyCartSnapshot]
  );

  /**
   * Agent path: replace UI cart from agent response payload.
   * Does NOT call the Cart API.
   */
  const replaceCartItemsFromAgent = useCallback(
    (payload) => {
      if (payload == null) {
        setCartItems([]);
        return true;
      }
      if (Array.isArray(payload) && payload.length === 0) {
        setCartItems([]);
        return true;
      }
      return applyCartSnapshot(payload);
    },
    [applyCartSnapshot]
  );

  /** GET /api/cart — hydrate after login / refresh. */
  const refreshCart = useCallback(async () => {
    if (!user?.customer_id) {
      setCartItems([]);
      return null;
    }
    setCartLoading(true);
    try {
      const apiRes = await fetchCart();
      if (apiRes) applyFromApiResponse(apiRes);
      return apiRes;
    } catch (error) {
      console.warn("[Cart] refreshCart failed:", error);
      return null;
    } finally {
      setCartLoading(false);
    }
  }, [user?.customer_id, applyFromApiResponse]);

  // Login / logout / page restore: load cart from API when authenticated.
  useEffect(() => {
    if (user?.customer_id) {
      refreshCart();
    } else {
      setCartItems([]);
    }
  }, [user?.customer_id, refreshCart]);

  // Also react to explicit login events.
  useEffect(() => {
    const onAuth = () => {
      const profile = getUserProfile();
      if (profile?.customer_id) {
        refreshCart();
      } else {
        setCartItems([]);
      }
    };
    window.addEventListener("authStateChanged", onAuth);
    return () => window.removeEventListener("authStateChanged", onAuth);
  }, [refreshCart]);

  /**
   * Manual add → POST /api/cart → mirror `data.cart`.
   *
   * `options.silent` suppresses the toast and the fly-to-bag animation — for
   * callers that use this to re-set a quantity rather than add something.
   */
  const addToCart = useCallback(
    async (product, options) => {
      if (!product?.id) return null;
      const quantity =
        typeof product.quantity === "number" && product.quantity > 0
          ? Math.floor(product.quantity)
          : 1;
      const size = extractItemSize(product);
      const sizeKey = normalizeCartSizeLabel(size);

      // Optimistic, and before the first await: the ghost must leave from the
      // card the shopper pressed, which a re-render may remove (moving a
      // wishlist row to the bag deletes that row).
      if (!options?.silent) {
        flyToBag("cart", {
          from: isAgentActionActive() ? "agent" : "pointer",
          image: productImageOf(product),
          key: `${product.id}:${sizeKey}`,
        });
      }

      queueCartUiEvent({
        action: "add",
        productId: product.id,
        productTitle: product?.title ?? product?.name ?? null,
        quantity,
      });

      try {
        const apiRes = await addCartLine({
          article_id: product.id,
          quantity,
          size,
        });
        if (apiRes) applyFromApiResponse(apiRes);
        else await refreshCart();
        if (!options?.silent) {
          showCartToast("add", product);
        }
        return apiRes;
      } catch (error) {
        console.warn("[Cart] addToCart API failed:", error);
        if (!options?.silent) {
          // The ghost already flew; say plainly that the add did not stick.
          showToast({
            id: "cart-add-error",
            title: "Couldn't add to cart",
            message: "Please try again.",
            severity: "error",
          });
        }
        await refreshCart();
        throw error;
      }
    },
    [queueCartUiEvent, applyFromApiResponse, refreshCart]
  );

  /** Manual remove → DELETE /api/cart → mirror `data.cart`. */
  const removeFromCart = useCallback(
    async (refOrId, options) => {
      const idx = lineIndexForRef(refOrId, cartItemsRef.current);
      if (idx < 0) return null;
      const target = cartItemsRef.current[idx];

      queueCartUiEvent({
        action: "remove",
        productId: target?.id,
        productTitle: target?.title ?? target?.name ?? null,
        quantity: null,
      });

      try {
        const apiRes = await removeCartLine({
          article_id: target.id,
          size: extractItemSize(target),
        });
        if (apiRes) applyFromApiResponse(apiRes);
        else await refreshCart();
        if (!options?.silent) {
          showCartToast("remove", target);
        }
        return apiRes;
      } catch (error) {
        console.warn("[Cart] removeFromCart API failed:", error);
        await refreshCart();
        throw error;
      }
    },
    [queueCartUiEvent, applyFromApiResponse, refreshCart]
  );

  /** Manual qty → PUT /api/cart (0 removes) → mirror `data.cart`. */
  const updateQuantity = useCallback(
    async (refOrId, quantity, options) => {
      if (quantity <= 0) {
        return removeFromCart(refOrId, options);
      }
      const idx = lineIndexForRef(refOrId, cartItemsRef.current);
      if (idx < 0) return null;
      const target = cartItemsRef.current[idx];

      queueCartUiEvent({
        action: "update_qty",
        productId: target?.id,
        productTitle: target?.title ?? target?.name ?? null,
        quantity,
      });

      try {
        const apiRes = await updateCartLine({
          article_id: target.id,
          quantity,
          size: extractItemSize(target),
        });
        if (apiRes) applyFromApiResponse(apiRes);
        else await refreshCart();
        return apiRes;
      } catch (error) {
        console.warn("[Cart] updateQuantity API failed:", error);
        await refreshCart();
        throw error;
      }
    },
    [queueCartUiEvent, applyFromApiResponse, refreshCart, removeFromCart]
  );

  const removeItemsByIds = useCallback(
    async (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) return;
      for (const id of ids) {
        try {
          await removeFromCart(id, { silent: true });
        } catch {
          /* continue */
        }
      }
    },
    [removeFromCart]
  );

  /** Manual clear → DELETE /api/cart?all=true. */
  const clearCart = useCallback(async (options) => {
    queueCartUiEvent({
      action: "clear",
      productId: null,
      productTitle: null,
      quantity: null,
    });
    try {
      const apiRes = await clearCartRemote();
      if (apiRes) applyFromApiResponse(apiRes);
      else setCartItems([]);
      if (!options?.silent) {
        showCartToast("clear");
      }
      return apiRes;
    } catch (error) {
      console.warn("[Cart] clearCart API failed:", error);
      // Still clear UI so checkout confirmation is not blocked
      setCartItems([]);
      throw error;
    }
  }, [queueCartUiEvent, applyFromApiResponse]);

  const totalItems = cartItems.reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );
  const totalPrice = cartItems.reduce(
    (sum, item) =>
      sum + (parseFloat(String(item.price)) || 0) * (Number(item.quantity) || 0),
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartLoading,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalItems,
        totalPrice,
        removeItemsByIds,
        replaceCartItemsFromAgent,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
}

// --- Auth cookie helpers (kept for callers that import from this module) ---
export const getAuthToken = () => {
  return document.cookie.replace(
    /(?:(?:^|.*;\s*)authToken\s*=\s*([^;]*).*$)|^.*$/,
    "$1"
  );
};

export const getUsername = () => {
  const match = document.cookie.match(/(?:^|;\s*)username\s*=\s*([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

export const getUserRole = () => {
  const match = document.cookie.match(/(?:^|;\s*)userRole\s*=\s*([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};
